import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { ALL_EXAMS, ALL_STATES } from '@cont/Compete/constants';
import styled from '@emotion/styled';

import { useIsMobile } from '@stores/AppCommonData';
import useCompete from '@stores/compete';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import { forwardRef, useEffect } from 'react';
import Filters from './Filters';
import Leaderboard from './Leaderboard';
import LeaderboardDialog from './LeaderboardDialog';
import useFilter from './useFilter';
import useLeaderboardWithPagination from './useLeaderboardWithPagination';

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_16B};

  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_10B};
    margin-bottom: ${Spacings.SPACING_10B};
  }
`;

const StyledFilters = styled(Filters)`
  & > :first-child {
    margin-right: ${Spacings.SPACING_4B};
  }
`;

const LeaderboardWithFilters = forwardRef(({ fallback }, ref) => {
  const isMobile = useIsMobile();

  const { isLeaderboardDialogOpen } = useCompete();
  const { examFilter, stateFilter, setStateFilter, setExamFilter } =
    useCompete();
  const { mutate: fetchFilterData } = useFilter();

  const { data, myData, rawData, mutate, setSize, size, isValidating } =
    useLeaderboardWithPagination(examFilter, stateFilter, {
      fallbackData: fallback
    });

  const { data: filterData } = useFilter();
  const { userPreference } = filterData ?? {};
  const { examPreferenceIds, stateCode } = userPreference ?? {};

  useEffect(() => {
    if (stateCode) {
      const selectedState = filterData?.filters.states?.find(
        (item) => item.code === stateCode
      );

      setStateFilter(selectedState ?? ALL_STATES);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateCode]);

  useEffect(() => {
    if (examPreferenceIds) {
      if (examPreferenceIds?.length === 1) {
        const selectedExam = filterData?.filters.exams?.find(
          (item) => item.id === examPreferenceIds[0]
        );

        setExamFilter(selectedExam ?? ALL_EXAMS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examPreferenceIds]);

  const getAccumulatedData = () => {
    if (rawData.length > 0) {
      return rawData?.reduce((accumulator, currentValue) => {
        return {
          results: [
            ...(accumulator.results ?? []),
            ...(currentValue?.leaderboardData?.leaderboard ?? [])
          ],
          next: !!currentValue?.leaderboardData?.meta?.nextOffset ?? null
        };
      }, []);
    }
    return [];
  };
  const accumulatedData = getAccumulatedData();

  const isLoggedIn = useMyInfo(getIsLoggedIn);

  useEffect(() => {
    if (isLoggedIn) {
      fetchFilterData();
    }
    mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <Wrapper ref={ref}>
      <Typography variant={isMobile ? 'h5' : 'h4'}>Leaderboard</Typography>
      <StyledFilters />
      <Leaderboard
        source="Home"
        leaderboard={data.leaderboard}
        myData={myData}
      />
      {isLeaderboardDialogOpen && (
        <LeaderboardDialog
          data={accumulatedData}
          isValidating={isValidating}
          size={size}
          setSize={setSize}
          myData={myData}
        />
      )}
    </Wrapper>
  );
});

LeaderboardWithFilters.displayName = LeaderboardWithFilters;

export default LeaderboardWithFilters;
